import { z } from 'zod'
import {
  ChangeId,
  EndDate,
  Id,
  IsActive,
  IsActual,
  NextId,
  ObjectId,
  ObjectUuid,
  OperationTypeId,
  PrevId,
  StartDate,
  UpdateDate,
} from './common-schemas'
import { GarItem } from './gar-item'

export namespace AddressObject {
  export interface Data {
    /** @description Unique identifier of the record. Key field */
    readonly id: number

    /** @description Global unique identifier of the address object */
    readonly objectId: number

    /** @description Global unique identifier of the address object of GUID type */
    readonly objectGuid: string

    /** @description ID of the changing transaction */
    readonly changeId: number

    /** @description Name */
    readonly name: string

    /** @description Brief name of the object type */
    readonly typeName: string

    /** @description Level of the address object */
    readonly level: string

    /** @description Status of action on the record – reason for the record's appearance */
    readonly operationTypeId: number

    /** @description Identifier of the record linking to the previous historical record */
    readonly prevId?: number

    /** @description Identifier of the record linking to the subsequent historical record */
    readonly nextId?: number

    /** @description Date of entry (update) of the record */
    readonly updateDate: Date

    /** @description Start date of the record */
    readonly startDate: Date

    /** @description End date of the record */
    readonly endDate: Date

    /** @description Status of relevance of the FIAS address object */
    readonly isActual: boolean

    /** @description Sign of an active address object */
    readonly isActive: boolean
  }
}

/** @description Address-forming element */
export class AddressObject extends GarItem<
  'AddressObject',
  AddressObject.Data
> {
  static readonly SCHEMA: GarItem.Schema<AddressObject.Data> = z
    .strictObject({
      ID: Id,

      OBJECTID: ObjectId,

      OBJECTGUID: ObjectUuid,

      CHANGEID: ChangeId,

      NAME: z.string().describe('Name'),

      TYPENAME: z.string().describe('Brief name of the object type'),

      LEVEL: z.string().describe('Level of the address object'),

      OPERTYPEID: OperationTypeId,

      PREVID: PrevId.optional(),

      NEXTID: NextId.optional(),

      UPDATEDATE: UpdateDate,

      STARTDATE: StartDate,

      ENDDATE: EndDate,

      ISACTUAL: IsActual,

      ISACTIVE: IsActive,
    })
    .transform<AddressObject.Data>(attributes => ({
      id: attributes.ID,
      objectId: attributes.OBJECTID,
      objectGuid: attributes.OBJECTGUID,
      changeId: attributes.CHANGEID,
      name: attributes.NAME,
      typeName: attributes.TYPENAME,
      level: attributes.LEVEL,
      operationTypeId: attributes.OPERTYPEID,
      prevId: attributes.PREVID,
      nextId: attributes.NEXTID,
      updateDate: attributes.UPDATEDATE,
      startDate: attributes.STARTDATE,
      endDate: attributes.ENDDATE,
      isActual: attributes.ISACTUAL,
      isActive: attributes.ISACTIVE,
    }))

  readonly type = 'AddressObject'

  protected get schema() {
    return AddressObject.SCHEMA
  }
}
